import { useEffect, useState } from 'react';

import { Delete02Icon } from '@hugeicons/core-free-icons';
import { useDispatch, useSelector } from 'react-redux';

import { BundleEdit } from './components/BundleEdit/BundleEdit';
import Alert from 'components/Alert/Alert';
import { List } from 'components/List/List';

import { getBrands, selectBrands } from 'api/brands-slice';
import {
  createBundle,
  deleteBundle,
  getBundles,
  selectBundles,
  updateBundle,
} from 'api/bundles-slice';
import { selectCompanyChecks } from 'api/company-checks-slice';
import { selectCompany } from 'api/company-slice';
import { getForms, selectForms } from 'api/forms-slice';

export const Bundles = ({ setLoading = () => {} }) => {
  const dispatch = useDispatch();

  const forms = useSelector(selectForms);
  const bundles = useSelector(selectBundles);
  const company = useSelector(selectCompany);
  const companyChecks = useSelector(selectCompanyChecks);
  const brands = useSelector(selectBrands);

  const onGetBrands = () => dispatch(getBrands());
  const onGetForms = () => dispatch(getForms());
  const onGetBundles = () => dispatch(getBundles());
  const onCreateBundle = data => dispatch(createBundle(data));
  const onBundleUpdate = (id, data) => dispatch(updateBundle({ id, data }));
  const onDeleteBundle = id => dispatch(deleteBundle(id));

  const [bundleId, setBundleId] = useState();
  const [showDeletionAlert, setShowDeletionAlert] = useState(false);

  // Initialize
  useEffect(() => {
    if (!company?.id) return;

    setLoading(true);
    Promise.all([onGetForms(), onGetBundles(), onGetBrands()]).then(_ => setLoading(false));
  }, [company]);

  // Create bundle
  const onCreate = async () => {
    setLoading(true);
    const { payload: newBundle } = await onCreateBundle({ name: 'New Bundle' });
    setBundleId(newBundle.id);
    setLoading(false);
  };

  // Update bundle
  const onUpdate = async data => {
    setLoading(true);

    // Clean details with no type
    data.checks_data = data.checks_data?.filter(check => check.type);

    await onBundleUpdate(bundleId, data);
    // Refetch bundles to get updated data
    await onGetBundles();
    setLoading(false);
  };

  const onDelete = async () => {
    setLoading(true);
    setShowDeletionAlert(false);
    setBundleId(null);
    await onDeleteBundle(bundleId);
    setLoading(false);
  };

  // Handle bundle selection with refetch
  const onBundleSelect = async newBundleId => {
    setLoading(true);
    setBundleId(newBundleId);
    // Refetch bundles to ensure we have the latest data
    await onGetBundles();
    setLoading(false);
  };

  return (
    <div className="u-flex-box">
      <Alert
        show={showDeletionAlert}
        title={`Are you sure you want to delete the bundle?`}
        type="error"
        icon={Delete02Icon}
        showConfirmationInput
        ok="Delete"
        onOk={onDelete}
        cancel="Cancel"
        onCancel={() => setShowDeletionAlert(false)}
      />
      <List
        title="Bundles"
        subtitle="Configure your companies bundles, which are groups of checks that can be launched together to save time."
        className="u-margin-right--large"
        onNew={onCreate}>
        <List.Category title="Bundles">
          {bundles.map(bundle => (
            <List.Item
              key={bundle.id}
              title={bundle.name}
              value={bundle.id}
              onClick={onBundleSelect}
            />
          ))}
        </List.Category>
      </List>
      {bundleId && (
        <BundleEdit
          bundle={bundles.find(bundle => bundle.id === bundleId)}
          company={company}
          companyChecks={companyChecks}
          brands={brands}
          forms={forms}
          onUpdate={onUpdate}
          onDelete={() => setShowDeletionAlert(true)}
          onClose={() => setBundleId(null)}
        />
      )}
    </div>
  );
};
